import type { FilterCategory } from '../../shared/components/filter-modal/filter-modal.component';

/** Customers with Discounts filter category id for the Customers page Filter modal. */
export const CUSTOMER_FILTER_CUSTOMERS_ID = 'customers';

/** Lightweight customer row used by the Customers with Discounts multi-select. */
export interface CustomerFilterListItem {
  id: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

/** Applied Filter modal state for Customers page (Customers with Discounts only). */
export interface CustomerDiscountPageFilters {
  /** Selected customer UUIDs (empty = no customer filter). */
  customers: string[];
}

export function createDefaultCustomerDiscountPageFilters(): CustomerDiscountPageFilters {
  return {
    customers: [],
  };
}

/** Customers with Discounts multi-select shell — options filled at runtime. */
export const CUSTOMER_FILTER_CUSTOMERS_CATEGORY: FilterCategory = {
  id: CUSTOMER_FILTER_CUSTOMERS_ID,
  name: 'Customers with Discounts',
  type: 'multi-dropdown',
  options: [],
};

function normalizeCustomerIds(ids: string[] | undefined | null): string[] {
  if (!Array.isArray(ids) || ids.length === 0) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const value = String(raw ?? '').trim();
    if (!value) {
      continue;
    }
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push(value);
  }
  return out;
}

/** Fresh Customers with Discounts category for the Filter modal. */
export function createCustomerDiscountFilterCategories(
  applied: CustomerDiscountPageFilters = createDefaultCustomerDiscountPageFilters()
): FilterCategory[] {
  const selectedCustomers = normalizeCustomerIds(applied.customers);
  return [
    {
      ...CUSTOMER_FILTER_CUSTOMERS_CATEGORY,
      // Seed selected ids so reopen/sync keeps applied customers before options load.
      options: selectedCustomers.map(id => ({
        id: `customers-${id}`,
        label: id,
        value: id,
        selected: true,
      })),
    },
  ];
}

function formatCustomerDisplayName(customer: {
  id: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): string {
  const fromName = String(customer.name ?? '').trim();
  if (fromName) {
    return fromName;
  }
  const first = String(customer.first_name ?? '').trim();
  const last = String(customer.last_name ?? '').trim();
  const combined = `${first} ${last}`.trim();
  return combined || customer.id;
}

/**
 * Build Customers with Discounts multi-select options for checkbox-multi-dropdown.
 * Clear is provided by the shared dropdown footer action, not as an option row.
 */
export function buildCustomerNamesFilterOptions(
  customers: CustomerFilterListItem[],
  selectedValues: string[] = []
): FilterCategory['options'] {
  const selectedSet = new Set(normalizeCustomerIds(selectedValues));

  const options: FilterCategory['options'] = [];
  const seen = new Set<string>();
  const sorted = [...customers].sort((a, b) =>
    formatCustomerDisplayName(a).localeCompare(formatCustomerDisplayName(b), undefined, {
      sensitivity: 'base',
    })
  );

  for (const customer of sorted) {
    const value = String(customer.id ?? '').trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({
      id: `customers-${value}`,
      label: formatCustomerDisplayName(customer),
      value,
      selected: selectedSet.has(value),
    });
  }

  for (const selected of selectedSet) {
    if (seen.has(selected)) {
      continue;
    }
    seen.add(selected);
    options.push({
      id: `customers-${selected}`,
      label: selected,
      value: selected,
      selected: true,
    });
  }

  return options;
}

/** Rebuild the Customers with Discounts category options from cached redeemers + selection. */
export function syncCustomerNamesFilterCategoryOptions(
  categories: FilterCategory[],
  customers: CustomerFilterListItem[],
  selectedValues?: string[]
): FilterCategory[] {
  const customersCategory = categories.find(
    category => category.id === CUSTOMER_FILTER_CUSTOMERS_ID
  );
  const selected =
    selectedValues ??
    normalizeCustomerIds(
      customersCategory?.options.filter(option => option.selected).map(option => option.value) ?? []
    );

  const nextCategory: FilterCategory = {
    ...(customersCategory ?? CUSTOMER_FILTER_CUSTOMERS_CATEGORY),
    id: CUSTOMER_FILTER_CUSTOMERS_ID,
    name: 'Customers with Discounts',
    type: 'multi-dropdown',
    options: buildCustomerNamesFilterOptions(customers, selected),
  };

  const withoutCustomers = categories.filter(
    category => category.id !== CUSTOMER_FILTER_CUSTOMERS_ID
  );
  return [nextCategory, ...withoutCustomers];
}

/** Read selected Customers with Discounts values from Filter modal categories. */
export function resolveCustomerDiscountPageFiltersFromCategories(
  categories: FilterCategory[]
): CustomerDiscountPageFilters {
  const customers = normalizeCustomerIds(
    categories
      .find(category => category.id === CUSTOMER_FILTER_CUSTOMERS_ID)
      ?.options.filter(option => option.selected)
      .map(option => option.value) ?? []
  );

  return { customers };
}

/** Map Customers with Discounts UI filter to the `id` API query token (comma-separated UUIDs). */
export function resolveCustomerIdQueryParam(
  filters: CustomerDiscountPageFilters
): string | undefined {
  const customers = normalizeCustomerIds(filters.customers);
  if (customers.length === 0) {
    return undefined;
  }
  return customers.join(',');
}

/** Count non-default Customers with Discounts selections for the Filter badge. */
export function countActiveCustomerDiscountFilters(filters: CustomerDiscountPageFilters): number {
  return normalizeCustomerIds(filters.customers).length > 0 ? 1 : 0;
}
